import React, { useEffect } from 'react';
import { useStatus } from './StatusContext.js';
import { cacheManager } from '../hooks/GlobalCacheManager.js';
import { getApiBase } from '../utils/apiBase.js';
import { globalCache } from '../hooks/useDataCache.js';
import { StatusEvaluator } from './StatusEvaluator.js';
import { LastGoodStatusStore } from '../utils/LastGoodStatusStore.js';

export const StatusMonitor = React.memo(() => {
  const { updatePageStatus } = useStatus();

  useEffect(() => {
    const API = getApiBase();
    const url = `${API}/data-updates?ts=${Date.now()}`;
    // Start periodic background refresh of changed artifacts across tiers
    try { cacheManager.startBackgroundRefresh(45000); } catch {}
    let lastEventTs = Date.now();
    
    // When the tab/app returns to the foreground, immediately refresh changed artifacts
    const handleVisibilityOrFocus = async () => {
      try {
        if (document.visibilityState === 'visible') {
          await cacheManager.updateByChangedArtifactsWithTiers();
          lastEventTs = Date.now();
        }
      } catch {}
    };
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    const deriveBoxStatusesFromCache = () => {
      try {
        // FTSO boxes
        const sum = globalCache.get('/submission_summary.json');
        const resDash = globalCache.get('/ftso_performance_dashboard.json');
        const submit2 = globalCache.get('/submit2prices.json');
        const feedCfg = globalCache.get('/feed_config.json');
        const entities = ['UYS','ENV'];
        const networks = ['flare','songbird'];
        if (sum && submit2 && feedCfg) {
          entities.forEach(entity => {
            networks.forEach(network => {
              try {
                const s = sum?.[entity]?.[network];
                const rm = resDash?.[entity]?.[network]?.detailed_metrics?.results_metrics;
                const p = submit2?.[entity]?.[network];
                const fc = feedCfg?.[entity]?.[network];
                const st = StatusEvaluator.evaluateFtsoSummaryBoxStatus(s, rm, p, fc);
                if (st) LastGoodStatusStore.set('ftso-submissions', `${entity}|${network}`, st);
              } catch {}
            });
          });
        }
        // FDC boxes
        const fdcSum = globalCache.get('/fdc_submission_summary.json');
        if (fdcSum) {
          entities.forEach(entity => {
            networks.forEach(network => {
              try {
                const s = fdcSum?.[entity]?.[network];
                const st = StatusEvaluator.evaluateFdcSummaryBoxStatus(s);
                if (st) LastGoodStatusStore.set('fdc-submissions', `${entity}|${network}`, st);
              } catch {}
            });
          });
        }
      } catch {}
    };

    const handleSSEUpdate = async (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'update' && data.status === 'success') {
          console.log('Status monitor received SSE update');
          lastEventTs = Date.now();
          await cacheManager.handleSSEUpdate(data);
          deriveBoxStatusesFromCache();
        }
      } catch (error) {
        console.error('Error processing SSE event in status monitor:', error);
      }
    };

    const eventSource = new EventSource(url);
    eventSource.onopen = async () => {
      try { await cacheManager.updateByChangedArtifactsWithTiers(); lastEventTs = Date.now(); deriveBoxStatusesFromCache(); } catch {}
    };
    eventSource.onerror = async () => {
      try { await cacheManager.updateByChangedArtifactsWithTiers(); lastEventTs = Date.now(); deriveBoxStatusesFromCache(); } catch {}
    };
    eventSource.addEventListener('update', handleSSEUpdate);
    eventSource.addEventListener('heartbeat', () => { lastEventTs = Date.now(); });

    // Watchdog: if neither heartbeat nor update for 35s, run a catch-up once
    const watchdog = setInterval(async () => {
      const now = Date.now();
      if (now - lastEventTs > 10000) {
        try { await cacheManager.updateByChangedArtifactsWithTiers(); lastEventTs = now; deriveBoxStatusesFromCache(); } catch {}
      }
    }, 5000);

    return () => {
      eventSource.removeEventListener('update', handleSSEUpdate);
      eventSource.close();
      try { cacheManager.stopBackgroundRefresh(); } catch {}
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      clearInterval(watchdog);
    };
  }, [updatePageStatus]);

  return null;
});